import { AxiosInstance } from "axios";
import { difference, union, unionBy, uniq } from "lodash";
import UsersService, { getCookie } from "@velocity-ehs/user-utils-module";
import { QueryConfig } from "../components/supportFunctions/graphql/Queries";

type UserDetails = {
  id: string;
  userDemographic: {
    displayName: string;
    firstName: string;
    lastName: string;
  };
};

class UsersStore {
  public ids: string[];
  public userDetails: UserDetails[];
  private api: AxiosInstance;

  constructor() {
    this.ids = [];
    this.userDetails = [];

    this.api = UsersService.api;
    this.addBearerTokenInterceptor();
  }

  public async setUsersDetails(
    data: any[],
    propKeys: string[],
    accessor?: Function
  ) {
    //The bridge does not return IDs in a useable format, so they have to be formatted first. This can be deleted when that is resolved.
    const formattedData = data?.map((item) => {
      return {
        ...item,
        createdBy: {
          id: formatToUUID(accessor(item)),
        },
      };
    });
    const newIds = this.getNewIds(formattedData, propKeys, accessor);

    if (newIds.length) {
      await this.getUsersDemographics(newIds);
    }

    return this.setDemographics(formattedData, propKeys, accessor);
  }

  private getNewIds(data: any[], propKeys: string[], accessor?: Function) {
    const userIds: string[] = [];
    for (let i = 0; i < data.length; i++) {
      if (!accessor) {
        propKeys.forEach((key) => {
          userIds.push(data[i][key]?.id || "");
        });
      }

      if (accessor) {
        userIds.push(accessor(data[i]));
      }
    }
    return difference(uniq(userIds), this.ids);
  }

  private async getUsersDemographics(ids: string[]) {
    this.ids = union(this.ids, ids);
    const users = await this.api.post(
      "",
      getUsersByIds(ids, this.getCustomerId())
    );
    this.userDetails = unionBy(
      this.userDetails,
      users.data.data.users.items,
      "id"
    );
  }

  private setDemographics(
    data: any[],
    propKeys: string[],
    accessor?: Function
  ) {
    return data?.map((item: any) => {
      if (!item.temp) item.temp = {};
      for (let i = 0; i < propKeys.length; i++) {
        item.temp[propKeys[i]] = this.userDetails.find((user) => {
          if (!accessor) {
            return user.id === item[propKeys[i]]?.id;
          }
          return user.id === accessor(item);
        });
      }
      return item;
    });
  }

  private getJwt() {
    return getCookie("accelerate.jwt") || "";
  }

  private getCustomerId() {
    return getCookie("customerid") || "";
  }

  private addBearerTokenInterceptor() {
    if (!this.api) return;
    this.api.interceptors.request.use((config) => {
      //@ts-ignore
      config.headers["Authorization"] = "Bearer " + this.getJwt();
      return config;
    });
  }
}

const getUsersByIds = (ids: string[], customerId: string): QueryConfig => {
  const query = `query getUsersForEHSConfigs($filter: UserFilter, $pagination: PaginationInput) {
      users(filter: $filter, paginationInput: $pagination) {
        items {
          id,
        userDemographic {
            firstName,
            lastName,
            displayName
          }
        },
      }
  }`;

  return {
    query: query,
    variables: {
      filter: { and: { customerId: [customerId], id: ids } },
      pagination: { pageSize: ids.length },
    },
  };
};

export default new UsersStore();

function formatToUUID(input: string[]) {
  if (input.length !== 32) {
    return input;
  }
  return `${input.slice(0, 8)}-${input.slice(8, 12)}-${input.slice(
    12,
    16
  )}-${input.slice(16, 20)}-${input.slice(20)}`;
}
